// Stundenuhren: was in einer Stunde in welcher Reihenfolge läuft.

import { useCallback, useEffect, useState } from "react";
import { api, musikregelApi, type MusikRegel } from "../api";
import { regelChips } from "../musikregeln-text";
import { Icon, errorText } from "../components/streaming-ui";
import { Callout, Card, EmptyState, Page, PageHeader } from "../components/ui";
import type { Channel, Clock, ClockSlot, ClockSlotKind, LibraryItem, Playlist } from "../types";

const KIND: Record<ClockSlotKind, { label: string; color: string; hint: string; minutes: number }> = {
  music: { label: "Musik", color: "#3b82f6", hint: "Ein Titel aus der Mediathek – auf Wunsch nur mit einem Tag, nach einer Musikregel oder aus einer Playlist.", minutes: 3.5 },
  jingle: { label: "Jingle", color: "#f59e0b", hint: "Ein zufälliger Jingle (nie derselbe zweimal hintereinander) oder ein bestimmter.", minutes: 0.15 },
  ad_break: { label: "Werbeblock", color: "#ef4444", hint: "Spots aus der Werbeplanung des Kanals, jeder Kunde höchstens einmal.", minutes: 1 },
  news: { label: "Nachrichten", color: "#10b981", hint: "Die neueste Datei der Kategorie „Nachrichten“ (nicht älter als 3 Stunden).", minutes: 3 },
  speech: { label: "Wortbeitrag", color: "#8b5cf6", hint: "Ein Beitrag der Kategorie „Wortbeitrag“, auf Wunsch mit Tag.", minutes: 4 },
  item: { label: "Fester Titel", color: "#6b7280", hint: "Immer derselbe Titel, z. B. eine Station-ID oder ein Opener.", minutes: 0.3 },
};

const emptySlot = (kind: ClockSlotKind): ClockSlot => ({ kind, tag: null, playlist_id: null, item_id: null, max_items: kind === "ad_break" ? 3 : null, label: null, rule_id: null, rule_name: null, rule_farbe: null });

/** Grobe Vorschau: welcher Anteil der Stunde auf welche Plätze fällt. */
function ClockDial({ slots, size = 180 }: { slots: ClockSlot[]; size?: number }): JSX.Element {
  const sw = Math.min(12, Math.max(6, size * 0.058)); // Ringbreite: dünn und ruhig
  const r = size / 2 - sw / 2 - 9; // außen Platz für die Viertelstunden-Striche
  const c = size / 2;
  const total = slots.reduce((s, x) => s + KIND[x.kind].minutes, 0);
  const musicMinutes = slots.filter((s) => s.kind === "music").reduce((s, x) => s + KIND[x.kind].minutes, 0);
  // Reicht die Uhr nicht, füllen die Musikplätze den Rest.
  const scale = total >= 60 || musicMinutes === 0 ? 60 / Math.max(total, 1) : 1;
  const p = (a: number) => `${(c + r * Math.cos(a)).toFixed(2)} ${(c + r * Math.sin(a)).toFixed(2)}`;
  const arc = (x0: number, x1: number): string => `M ${p(x0)} A ${r} ${r} 0 ${x1 - x0 > Math.PI ? 1 : 0} 1 ${p(x1)}`;
  const ruleW = Math.max(2, sw * 0.3);
  const arcAt = (rr: number, x0: number, x1: number): string => {
    const q = (a: number) => `${(c + rr * Math.cos(a)).toFixed(2)} ${(c + rr * Math.sin(a)).toFixed(2)}`;
    return `M ${q(x0)} A ${rr} ${rr} 0 ${x1 - x0 > Math.PI ? 1 : 0} 1 ${q(x1)}`;
  };
  // Segmente als gerade abgeschnittene Bögen mit kleinem Spalt. Kurze Plätze
  // (Jingle, Station-ID) bekommen eine Mindestlänge, damit man sie sieht; wird
  // es dadurch mehr als eine Stunde, schrumpft alles anteilig.
  const gap = slots.length > 1 ? Math.max(0.03, 3 / r) : 0;
  const minA = Math.max(0.06, (sw * 0.9) / r) + gap;
  const raw = slots.map((s) => Math.max((KIND[s.kind].minutes * (total >= 60 ? scale : 1) / 60) * 2 * Math.PI, minA));
  const sum = raw.reduce((x, y) => x + y, 0);
  const widths = sum > 2 * Math.PI ? raw.map((w) => (w / sum) * 2 * Math.PI) : raw;
  let angle = -Math.PI / 2;
  const arcs = slots.map((s, i) => {
    const a0 = angle;
    const a1 = angle + widths[i]!;
    angle = a1;
    const title = s.label || (s.rule_name ? `${KIND[s.kind].label} · ${s.rule_name}` : KIND[s.kind].label);
    return (
      <g key={i}>
        <path d={arc(a0 + gap / 2, a1 - gap / 2)} fill="none" stroke={KIND[s.kind].color} strokeWidth={sw} strokeLinecap="butt"><title>{title}</title></path>
        {/* Musikregel: ein schmaler Innenring in ihrer Farbe. */}
        {s.kind === "music" && s.rule_farbe && (
          <path d={arcAt(r - sw / 2 - ruleW / 2 - 1.5, a0 + gap / 2, a1 - gap / 2)} fill="none" stroke={s.rule_farbe} strokeWidth={ruleW} strokeLinecap="butt"><title>{title}</title></path>
        )}
      </g>
    );
  });
  const restStart = angle;
  const showRest = slots.length > 0 && total < 60 && musicMinutes > 0 && restStart < (3 * Math.PI) / 2 - gap;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0" role="img" aria-label={`Stundenuhr mit ${slots.length} Plätzen`}>
      {/* Spur */}
      <circle cx={c} cy={c} r={r} fill="none" strokeWidth={sw} style={{ stroke: "var(--bg-sunken)" }} />
      {/* Viertelstunden, dezent außen */}
      {[0, 1, 2, 3].map((q) => {
        const a = -Math.PI / 2 + (q * Math.PI) / 2;
        const r0 = r + sw / 2 + 2.5;
        const r1 = r0 + Math.max(3, size * 0.022);
        return <line key={q} x1={c + r0 * Math.cos(a)} y1={c + r0 * Math.sin(a)} x2={c + r1 * Math.cos(a)} y2={c + r1 * Math.sin(a)} strokeWidth={1.25} strokeLinecap="round" style={{ stroke: "var(--border-strong)" }} />;
      })}
      {showRest && (
        <path d={arc(restStart + gap / 2, (3 * Math.PI) / 2 - gap / 2)} fill="none" stroke={KIND.music.color} strokeOpacity={0.25} strokeWidth={sw} strokeLinecap="butt" />
      )}
      {arcs}
      <text x={c} y={c - (size >= 150 ? 2 : 1)} textAnchor="middle" style={{ fill: "var(--text-primary)", fontSize: size >= 150 ? 20 : 15, fontWeight: 650 }} className="tabular-nums">{slots.length}</text>
      <text x={c} y={c + (size >= 150 ? 16 : 13)} textAnchor="middle" style={{ fill: "var(--text-muted)", fontSize: size >= 150 ? 11 : 9.5 }}>
        {slots.length === 1 ? "Platz" : "Plätze"}
      </text>
      {size >= 150 && (
        <text x={c} y={c + 31} textAnchor="middle" style={{ fill: "var(--text-muted)", fontSize: 10 }}>{total < 60 && musicMinutes > 0 ? "Rest: Musik" : `≈ ${Math.round(total)} Min.`}</text>
      )}
    </svg>
  );
}

/** Art eines Platzes als Chip: Farbpunkt + Wort. */
function KindChip({ kind, children, title, ruleColor }: { kind: ClockSlotKind; children?: React.ReactNode; title?: string; ruleColor?: string | null }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-sunken)] px-2 py-0.5 text-[0.72rem] font-medium text-secondary ring-1 ring-[var(--border)]" title={title}>
      <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: KIND[kind].color }} aria-hidden="true" />
      {children ?? KIND[kind].label}
      {ruleColor && <span className="-ml-0.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ background: ruleColor }} aria-hidden="true" />}
    </span>
  );
}

export function ClocksPage(): JSX.Element {
  const [clocks, setClocks] = useState<Clock[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [editing, setEditing] = useState<{ id: string | null; name: string; color: string; slots: ClockSlot[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, ch] = await Promise.all([api.listClocks(), api.listChannels()]);
      setClocks(c);
      setChannels(ch);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);
  useEffect(() => void load(), [load]);

  const save = async (): Promise<void> => {
    if (!editing) return;
    setError(null);
    try {
      const input = { name: editing.name, color: editing.color, slots: editing.slots };
      if (editing.id) await api.updateClock(editing.id, input);
      else await api.createClock(input);
      setNotice(editing.id ? "Gespeichert. Die kommenden Stunden werden neu geplant (von Hand eingefügte Titel bleiben)." : "Stundenuhr angelegt. Jetzt einem Kanal oder dem Sendeplan zuordnen.");
      setEditing(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <Page>
      <PageHeader
        eyebrow="Sendebetrieb"
        title="Stundenuhren"
        description={
          <>
            Eine Stundenuhr legt fest, was in einer Stunde nacheinander läuft – zum Beispiel Nachrichten, Jingle, zwei Titel, Werbeblock.
            Die Automatik plant damit die nächsten Stunden voraus. Ist die Uhr kürzer als eine Stunde, füllt sie mit ihren Musikplätzen auf;
            zur vollen Stunde beginnt immer die nächste.
          </>
        }
        actions={
          !editing && (
            <button onClick={() => setEditing({ id: null, name: "", color: "#ff5500", slots: [emptySlot("news"), emptySlot("jingle"), emptySlot("music"), emptySlot("music"), emptySlot("jingle"), emptySlot("music")] })}
              className="btn btn-primary">
              <Icon name="plus" /> Neue Stundenuhr
            </button>
          )
        }
      />
      <div className="space-y-6">
      {error && <Callout tone="danger">{error}</Callout>}
      {notice && <Callout tone="success">{notice}</Callout>}

      {editing ? (
        <ClockEditor value={editing} onChange={setEditing} onSave={save} onCancel={() => setEditing(null)} />
      ) : clocks.length === 0 ? (
        <EmptyState
          icon={<Icon name="clock" />}
          title="Noch keine Stundenuhr."
          description="Ohne Uhr spielt die Automatik einfach Musik aus Playlist oder Mediathek – das ist völlig in Ordnung. Eine Uhr lohnt sich, sobald Jingles, Nachrichten oder Werbung zu festen Plätzen laufen sollen."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {clocks.map((c) => (
            <section key={c.id} className="card flex flex-col">
              <div className="flex min-w-0 gap-5 p-5">
                <ClockDial slots={c.slots} size={124} />
                <div className="min-w-0 flex-1 space-y-2.5">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: c.color }} aria-hidden="true" />
                    <h2 className="truncate text-[0.95rem] font-semibold text-primary">{c.name}</h2>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {c.slots.map((s, i) => (
                      <KindChip key={i} kind={s.kind} ruleColor={s.rule_farbe} title={s.rule_name ? `Nach Musikregel „${s.rule_name}“` : undefined}>
                        {s.label || KIND[s.kind].label}{s.rule_name ? ` · ${s.rule_name}` : ""}{s.tag ? ` · ${s.tag}` : ""}
                      </KindChip>
                    ))}
                  </div>
                  <div className="text-xs text-muted">
                    {c.default_for.length > 0 ? `Standard für: ${c.default_for.join(", ")}` : "Keinem Kanal als Standard zugeordnet"}
                    {c.schedule_blocks > 0 ? ` · ${c.schedule_blocks} Sendeplan-Blöcke` : ""}
                  </div>
                </div>
              </div>
              <div className="mt-auto flex flex-wrap gap-2 border-t border-default px-5 py-3">
                <button onClick={() => setEditing({ id: c.id, name: c.name, color: c.color, slots: c.slots.map(({ position: _p, item_title: _t, playlist_name: _n, ...s }) => s) })}
                  className="btn btn-secondary btn-sm">Bearbeiten</button>
                <button onClick={() => window.confirm(`Stundenuhr „${c.name}" löschen? Kanäle mit dieser Uhr spielen danach nur Musik.`) && void api.deleteClock(c.id).then(load).catch((err) => setError(errorText(err)))}
                  className="btn btn-ghost btn-sm ml-auto text-red-600 dark:text-red-400"><Icon name="trash" className="h-3.5 w-3.5" /> Löschen</button>
              </div>
            </section>
          ))}
        </div>
      )}

      {!editing && clocks.length > 0 && (
        <Card
          title="Welche Uhr gilt?"
          description="Die Standard-Uhr eines Kanals gilt rund um die Uhr. Für einzelne Zeiten lässt sich im Sendeplan eine andere Uhr (oder eine Playlist) eintragen – das hat Vorrang."
          padded={false}
        >
          <div className="divide-y divide-[var(--border)]">
            {channels.map((ch) => <DefaultClockSelect key={ch.id} channel={ch} clocks={clocks} onSaved={load} onError={setError} />)}
          </div>
        </Card>
      )}
      </div>
    </Page>
  );
}

function DefaultClockSelect({ channel, clocks, onSaved, onError }: { channel: Channel; clocks: Clock[]; onSaved: () => Promise<void>; onError: (m: string) => void }): JSX.Element {
  const current = clocks.find((c) => c.default_for.includes(channel.name));
  return (
    <label className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm text-secondary">
      <span className="font-medium text-primary">{channel.name}</span>
      <select
        value={current?.id ?? ""}
        onChange={(e) => void api.setDefaultClock(channel.slug, e.target.value || null).then(onSaved).catch((err) => onError(errorText(err)))}
        className="input !w-auto min-w-[12rem]"
      >
        <option value="">Keine – nur Musik</option>
        {clocks.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </label>
  );
}

function ClockEditor({ value, onChange, onSave, onCancel }: {
  value: { id: string | null; name: string; color: string; slots: ClockSlot[] };
  onChange: (v: { id: string | null; name: string; color: string; slots: ClockSlot[] }) => void;
  onSave: () => Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [regeln, setRegeln] = useState<MusikRegel[]>([]);
  useEffect(() => {
    void musikregelApi.liste().then((r) => setRegeln(r.regeln)).catch(() => undefined);
    void (async () => {
      const channels = await api.listChannels().catch(() => []);
      const lists = await Promise.all(channels.map((c) => api.listPlaylists(c.slug).catch(() => [])));
      setPlaylists(lists.flat());
      setItems(await api.listLibrary({ limit: 200 }).catch(() => []));
    })();
  }, []);

  const setSlot = (i: number, patch: Partial<ClockSlot>): void => onChange({ ...value, slots: value.slots.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const move = (i: number, dir: -1 | 1): void => {
    const j = i + dir;
    if (j < 0 || j >= value.slots.length) return;
    const slots = [...value.slots];
    [slots[i], slots[j]] = [slots[j]!, slots[i]!];
    onChange({ ...value, slots });
  };

  return (
    <div className="card flex flex-col gap-6 p-5 lg:flex-row">
      <div className="flex flex-col items-center gap-4 lg:w-60 lg:border-r lg:border-default lg:pr-6">
        <ClockDial slots={value.slots} size={200} />
        <div className="flex max-w-[15rem] flex-wrap justify-center gap-1.5">
          {(Object.keys(KIND) as ClockSlotKind[]).map((k) => (
            <KindChip key={k} kind={k} title={KIND[k].hint} />
          ))}
        </div>
      </div>
      <div className="min-w-0 flex-1 space-y-4">
        <label className="block text-[0.8rem] font-medium text-secondary">
          Name
          <input className="input mt-1.5" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} placeholder="z. B. Tagesprogramm" />
        </label>
        <ol className="space-y-2">
          {value.slots.map((s, i) => (
            <li key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-default bg-card py-2 pl-2 pr-2 shadow-sm" style={{ boxShadow: `inset 3px 0 0 ${KIND[s.kind].color}` }}>
              <span className="w-6 text-right text-xs font-semibold tabular-nums text-muted">{i + 1}</span>
              <span className="relative inline-flex items-center">
                <span className="pointer-events-none absolute left-2.5 h-2 w-2 rounded-full" style={{ background: KIND[s.kind].color }} aria-hidden="true" />
                <select value={s.kind} onChange={(e) => setSlot(i, { ...emptySlot(e.target.value as ClockSlotKind) })}
                  className="input !h-8 !w-auto !pl-6 !text-[0.8rem] font-medium" title={KIND[s.kind].hint}>
                  {(Object.keys(KIND) as ClockSlotKind[]).map((k) => <option key={k} value={k}>{KIND[k].label}</option>)}
                </select>
              </span>
              {(s.kind === "music" || s.kind === "speech") && (
                <input className="input !h-8 !w-28 !text-xs" placeholder="Tag (optional)" value={s.tag ?? ""} onChange={(e) => setSlot(i, { tag: e.target.value || null })} title="Nur Titel mit diesem Tag, z. B. „neu“ oder „80er“" />
              )}
              {s.kind === "music" && (
                <span className="relative inline-flex max-w-full items-center">
                  {s.rule_id && s.rule_farbe && <span className="pointer-events-none absolute left-2.5 h-2 w-2 rounded-full" style={{ background: s.rule_farbe }} aria-hidden="true" />}
                  <select className={`input !h-8 !w-auto max-w-[14rem] !text-xs ${s.rule_id && s.rule_farbe ? "!pl-6" : ""}`}
                    value={s.playlist_id ? `p:${s.playlist_id}` : s.rule_id ? `r:${s.rule_id}` : ""}
                    title={(() => { const r = regeln.find((x) => x.id === s.rule_id); return r ? `${r.name}: ${regelChips(r.bedingungen).join(", ") || "alle Musik"} – ${r.anzahl} Titel` : undefined; })()}
                    onChange={(e) => {
                      const v = e.target.value;
                      const r = v.startsWith("r:") ? regeln.find((x) => x.id === v.slice(2)) : undefined;
                      setSlot(i, {
                        playlist_id: v.startsWith("p:") ? v.slice(2) : null,
                        rule_id: r?.id ?? null, rule_name: r?.name ?? null, rule_farbe: r?.farbe ?? null,
                      });
                    }}>
                    <option value="">aus der ganzen Mediathek</option>
                    {regeln.length > 0 && (
                      <optgroup label="nach Musikregel">
                        {regeln.map((r) => <option key={r.id} value={`r:${r.id}`}>nach Regel „{r.name}“ ({r.anzahl})</option>)}
                      </optgroup>
                    )}
                    {playlists.length > 0 && (
                      <optgroup label="aus einer Playlist">
                        {playlists.map((p) => <option key={p.id} value={`p:${p.id}`}>aus Playlist „{p.name}“</option>)}
                      </optgroup>
                    )}
                  </select>
                </span>
              )}
              {(s.kind === "item" || s.kind === "jingle" || s.kind === "news") && (
                <select className="input !h-8 !w-auto max-w-[16rem] !text-xs" value={s.item_id ?? ""} onChange={(e) => setSlot(i, { item_id: e.target.value || null })}>
                  <option value="">{s.kind === "item" ? "Titel wählen …" : s.kind === "jingle" ? "zufälliger Jingle" : "neueste Nachrichten"}</option>
                  {items.filter((it) => s.kind === "item" || it.category === (s.kind === "jingle" ? "jingle" : "news")).map((it) => <option key={it.id} value={it.id}>{it.title}</option>)}
                </select>
              )}
              {s.kind === "ad_break" && (
                <label className="flex items-center gap-1 text-xs text-secondary">
                  höchstens
                  <input type="number" min={1} max={10} className="input !h-8 !w-14 !px-2 !text-xs tabular-nums" value={s.max_items ?? 3} onChange={(e) => setSlot(i, { max_items: Number(e.target.value) })} />
                  Spots
                </label>
              )}
              <span className="flex-1" />
              <span className="flex items-center">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="btn btn-ghost btn-sm !w-7 !px-0" aria-label="nach oben"><Icon name="up" className="h-3.5 w-3.5" /></button>
                <button onClick={() => move(i, 1)} disabled={i === value.slots.length - 1} className="btn btn-ghost btn-sm !w-7 !px-0" aria-label="nach unten"><Icon name="down" className="h-3.5 w-3.5" /></button>
                <button onClick={() => onChange({ ...value, slots: value.slots.filter((_, j) => j !== i) })} className="btn btn-ghost btn-sm !w-7 !px-0 text-red-600 dark:text-red-400" aria-label="entfernen"><Icon name="x" className="h-3.5 w-3.5" /></button>
              </span>
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs text-muted">Platz anhängen:</span>
          {(Object.keys(KIND) as ClockSlotKind[]).map((k) => (
            <button key={k} onClick={() => onChange({ ...value, slots: [...value.slots, emptySlot(k)] })}
              className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[var(--border-strong)] px-2.5 py-1 text-xs font-medium text-secondary hover:border-solid hover:bg-[var(--bg-hover)]" title={KIND[k].hint}>
              <span className="h-2 w-2 rounded-full" style={{ background: KIND[k].color }} aria-hidden="true" />
              + {KIND[k].label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 border-t border-default pt-4">
          <button onClick={() => void onSave()} disabled={!value.name || value.slots.length === 0} className="btn btn-primary">Speichern</button>
          <button onClick={onCancel} className="btn btn-ghost">Abbrechen</button>
        </div>
      </div>
    </div>
  );
}
